import { disabledANDs, disabledOR } from "doc/formula/formulaConditions";
import "doc/formula/formulaTools";
import { defaultExteriorPropCatalog } from "../../elements/propCatalog/exteriorPropCatalog";
import { defaultInteriorPropCatalog } from "../../elements/propCatalog/interiorPropCatalog";
import type { DocMigration } from "../docMigration";

const builtInPropsGroupId = "builtInPropsGroup";

const builtInPropsGroup = {
  entityType: "GROUP",
  name: "Built-in Props",
  id: builtInPropsGroupId,
  sortIndex: 0,
  isBuiltIn: true,
  isDeletable: true,
};

// These keys were duplicated in the interior and exterior prop catalogs.
const duplicateKeys = [
  "sedan",
  "suv",
  "truck",
  "exteriorLamp",
  "rakeBracket",
  "angleBracket14x18",
  "angleBracket25x27",
  "angleBracket20x20",
  "soffitCorbel8x12",
  "awningBracket",
  "craftColumn",
  "shutters3050",
  "exteriorLampGas",
  "craftShutter",
  "elliottGableArch12",
  "elliottGableArch7",
  "elliottGableArch8",
  "elliottGableArch9",
];

interface Group {
  entityType: string;
  id: string;
  name: string;
  sortIndex?: number;
}

const newIdSuffix = "Group";

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_457: DocMigration = {
  maxSchemaVersion: "0.0.457",
  description: "Move prop attributes from settings to the props catalog",
  apply(json: any) {
    const { settings, propInstances, entityGroups, groups, settingsSets } = json;

    const settingsIds = Object.keys(settings);
    json.backupPropSettingsSets = {};
    for (const settingsId of settingsIds) {
      const setting = settings[settingsId];
      if (setting.type === "EXTERIOR_PROP_SETTINGS" || setting.type === "INTERIOR_PROP_SETTINGS") {
        json.backupPropSettingsSets[settingsId] = setting;
      }
    }

    let entityRefs = json.entityRefs;
    if (!entityRefs) {
      json.entityRefs = {};
      entityRefs = json.entityRefs;
    }

    for (const propInstanceId in propInstances) {
      const propInstance = propInstances[propInstanceId];
      if (duplicateKeys.includes(propInstance.propType) && propInstance.catalogType === "INTERIOR_PROP_CATALOG") {
        propInstance.propType = `${propInstance.propType}Interior`;
        propInstance.isExterior = false;
      }
    }

    for (const settingsId of settingsIds) {
      const setting = settings[settingsId];
      if (setting.type !== "EXTERIOR_PROP_SETTINGS" && setting.type !== "INTERIOR_PROP_SETTINGS") {
        continue;
      }
      const settingsSet = settingsSets[setting.parentId];
      if (!settingsSet || setting.type !== "INTERIOR_PROP_SETTINGS" || !setting.propTypes) {
        continue;
      }
      const duplicateIds = Object.keys(setting.propTypes).filter((key) => duplicateKeys.includes(key));
      for (const id of duplicateIds) {
        const propType = setting.propTypes[id];
        const newId = `${propType.id}Interior`;
        propType.id = newId;
        setting.propTypes[newId] = propType;
        delete setting.propTypes[id];
      }
    }

    let allPropAttributes = {};
    const settingsSetGroups: Map<string, Group> = new Map();
    const settingsSetGroupItems = new Map<string, string[]>();
    const propSettingsSetIds = {};
    const isPropExterior = {};
    for (const settingsId of settingsIds) {
      const setting = settings[settingsId];
      if (setting.type !== "EXTERIOR_PROP_SETTINGS" && setting.type !== "INTERIOR_PROP_SETTINGS") {
        continue;
      }
      const propAttributes = {};
      const settingsSet = settingsSets[setting.parentId];
      if (!settingsSet || !setting.propTypes) {
        continue;
      }
      let setGroupId = "";
      const settingDocRef = entityRefs[setting.parentId];
      if (Object.keys(setting.propTypes).length > 0) {
        for (const id in setting.propTypes) {
          propAttributes[id] = setting.propTypes[id];
          propAttributes[id].entityType = "PROP_ATTRIBUTES";
          if (!propSettingsSetIds[id]) {
            propSettingsSetIds[id] = [];
          }
          propSettingsSetIds[id].push(settingsSet.id);
          isPropExterior[id] = setting.type === "EXTERIOR_PROP_SETTINGS";
          // By default, exterior props are included in the foundation plan dimensions.
          if (isPropExterior[id]) {
            propAttributes[id].excludeFromFoundationPlanDimensions = false;
          }

          // If the settings are doc-reffed, doc-ref each prop attribute.
          if (settingDocRef) {
            entityRefs[id] = {
              ...settingDocRef,
              id,
              entityType: "PROP_ATTRIBUTES",
            };
          }
        }

        if (!settingsSetGroups[settingsSet.id]) {
          setGroupId = settingsSet.id + newIdSuffix;
          const group = {
            entityType: "GROUP",
            id: setGroupId,
            name: settingsSet.name,
          };
          groups[setGroupId] = group;
          settingsSetGroups[settingsSet.id] = group;
          settingsSetGroupItems.set(settingsSet.id, []);

          // If the settings are doc-reffed, doc-ref the group.
          if (settingDocRef) {
            entityRefs[setGroupId] = {
              ...settingDocRef,
              id: setGroupId,
              entityType: "GROUP",
            };
          }
        }
        for (const id in setting.propTypes) {
          entityGroups[`propAttributes.${id}`] = settingsSetGroups[settingsSet.id].id;
          settingsSetGroupItems.get(settingsSet.id)?.push(id);
        }
      }
      allPropAttributes = { ...allPropAttributes, ...propAttributes };
    }
    // Alphabetize the groups.
    const settingsSetGroupsArray = Object.values(settingsSetGroups);
    settingsSetGroupsArray.sort((a, b) => a.name.localeCompare(b.name));
    settingsSetGroupsArray.forEach((group, index) => {
      group.sortIndex = index;
    });
    // Alphabetize the items in each group.
    settingsSetGroupItems.forEach((items) => {
      const propAttributes = items.map((id) => allPropAttributes[id]);
      propAttributes.sort((a, b) => a.name.localeCompare(b.name));
      propAttributes.forEach((propAttribute, index) => {
        propAttribute.sortIndex = index;
      });
    });

    const propSettingDisabled = {};
    Object.keys(propSettingsSetIds).forEach((propId) => {
      // Built-in props are not disabled by settings.
      if (defaultExteriorPropCatalog[propId] || defaultInteriorPropCatalog[propId]) {
        propSettingDisabled[propId] = false;
        return;
      }
      const settingsSetIds = propSettingsSetIds[propId];
      const conditionals = settingsSetIds.map((settingsSetId) => {
        const settingsSet = settingsSets[settingsSetId];
        if (settingsSet.disabled) {
          return settingsSet.disabled;
        }
        return false;
      });
      const disabled = disabledANDs(conditionals);
      propSettingDisabled[propId] = disabled;
    });

    for (const propInstanceId in propInstances) {
      const propInstance = propInstances[propInstanceId];
      propInstance.catalogType = "PROP_CATALOG";
      const disabledFromSettings = propSettingDisabled[propInstance.propType];
      const isExterior = isPropExterior[propInstance.propType];

      if (disabledFromSettings) {
        if (propInstance.disabled) {
          const formula = disabledOR(propInstance.disabled, disabledFromSettings);
          propInstance.disabled = formula;
        } else {
          propInstance.disabled = disabledFromSettings;
        }
      }
      propInstance.isExterior = isExterior;
    }

    let addBuiltInProps = false;
    for (const propInstanceId in propInstances) {
      const propInstance = propInstances[propInstanceId];
      if (!allPropAttributes[propInstance.propType]) {
        addBuiltInProps = true;
        if (defaultExteriorPropCatalog[propInstance.propType]) {
          propInstance.isExterior = true;
        } else if (defaultInteriorPropCatalog[propInstance.propType]) {
          propInstance.isExterior = false;
        } else {
          console.warn(`Unknown prop type: ${propInstance.propType}`);
        }
      }
    }
    if (addBuiltInProps) {
      groups[builtInPropsGroupId] = builtInPropsGroup;
    }

    for (const settingsId of settingsIds) {
      const setting = settings[settingsId];
      if (setting.type === "EXTERIOR_PROP_SETTINGS" || setting.type === "INTERIOR_PROP_SETTINGS") {
        delete settings[settingsId];
      }
    }

    json.propAttributes = { ...(json.propAttributes ?? {}), ...allPropAttributes };

    return json;
  },
};
export default migration_0_0_457;
